import React, { useState, useEffect, useRef, useCallback } from "react";

import apiService from "../services/api";
import {
  getFollowedTeamNames,
  getTeamFollowEntry,
  recordAlert,
  hasAlertBeenShown,
} from "../utils/sportsPreferences";
import webPlayerStorage from "../utils/webPlayerStorage";

const ALERT_TYPES = {
  MATCH_START: {
    icon: "fa-play-circle",
    accent: "border-blue-500/50",
    bg: "bg-blue-500/10",
  },
  SCORE_CHANGE: {
    icon: "fa-futbol",
    accent: "border-green-500/50",
    bg: "bg-green-500/10",
  },
  FINAL_SCORE: {
    icon: "fa-flag-checkered",
    accent: "border-white/30",
    bg: "bg-white/5",
  },
  PLAYER_GOAL: {
    icon: "fa-star",
    accent: "border-amber-500/50",
    bg: "bg-amber-500/10",
  },
  PLAYER_LINEUP: {
    icon: "fa-list-ol",
    accent: "border-blue-400/50",
    bg: "bg-blue-400/10",
  },
};

const SportsAlertBanner = ({ enabled = true, pollIntervalMs = 15000 }) => {
  const [alerts, setAlerts] = useState([]);
  const prevScores = useRef({});
  const prevStatuses = useRef({});

  const dismissAlert = useCallback((id) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const addAlert = useCallback((alert) => {
    if (hasAlertBeenShown(alert.id)) return;
    recordAlert(alert.id);
    setAlerts((prev) => {
      const next = [alert, ...prev].slice(0, 2);

      return next;
    });

    const dismissMs =
      alert.type === "MATCH_START" || alert.type === "FINAL_SCORE"
        ? 8000
        : 5000;

    setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
    }, dismissMs);
  }, []);

  const checkForAlerts = useCallback(async () => {
    const followedTeams = getFollowedTeamNames();

    if (followedTeams.length === 0) return;

    const playlist = webPlayerStorage.getPlaylist();

    if (!playlist) return;

    try {
      const tz =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
      const res = await apiService.webPlayerRequest(
        `/v2/sports/home?timestamp=${Date.now()}&timezone=${encodeURIComponent(tz)}`
      );

      const allGames = [
        ...(res?.data?.live || []),
        ...(res?.data?.upcoming || []),
        ...(res?.data?.finished || []),
      ];

      for (const game of allGames) {
        const home = game.homeTeam;
        const away = game.awayTeam;
        const isFollowedHome = followedTeams.includes(home);
        const isFollowedAway = followedTeams.includes(away);

        if (!isFollowedHome && !isFollowedAway) continue;

        const followedName = isFollowedHome ? home : away;
        const entry = getTeamFollowEntry(followedName);
        const notifs = entry?.notifications || {};
        const gameKey = game.id || `${home}-${away}`;

        // Match start detection
        if (game.rating === "LIVE" && !prevStatuses.current[gameKey]) {
          if (notifs.matchStart !== false) {
            addAlert({
              id: `start-${gameKey}-${Date.now()}`,
              type: "MATCH_START",
              message: `${away} vs ${home} starting now`,
              logo: isFollowedHome ? game.homeTeamLogo : game.awayTeamLogo,
              teamName: followedName,
            });
          }
        }
        prevStatuses.current[gameKey] = game.rating;

        // Score change detection
        if (game.rating === "LIVE") {
          const currentScore = `${game.homeTeamScore || 0}-${game.awayTeamScore || 0}`;
          const prevScore = prevScores.current[gameKey];

          if (
            prevScore &&
            prevScore !== currentScore &&
            notifs.scoreChange !== false
          ) {
            addAlert({
              id: `score-${gameKey}-${currentScore}-${Date.now()}`,
              type: "SCORE_CHANGE",
              message: `GOAL! ${away} ${game.awayTeamScore || 0}-${game.homeTeamScore || 0} ${home}`,
              logo: isFollowedHome ? game.homeTeamLogo : game.awayTeamLogo,
              teamName: followedName,
            });
          }
          prevScores.current[gameKey] = currentScore;
        }

        // Final score detection
        if (
          game.rating === "FT" &&
          prevStatuses.current[gameKey] === "LIVE" &&
          notifs.finalScore !== false
        ) {
          addAlert({
            id: `final-${gameKey}-${Date.now()}`,
            type: "FINAL_SCORE",
            message: `Final: ${away} ${game.awayTeamScore || 0}-${game.homeTeamScore || 0} ${home}`,
            logo: isFollowedHome ? game.homeTeamLogo : game.awayTeamLogo,
            teamName: followedName,
          });
        }
      }
    } catch (err) {
      console.warn("SportsAlertBanner poll error:", err);
    }
  }, [addAlert]);

  useEffect(() => {
    if (!enabled) return;

    // Initial check
    const timeout = setTimeout(checkForAlerts, 3000);
    const interval = setInterval(checkForAlerts, pollIntervalMs);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [enabled, pollIntervalMs, checkForAlerts]);

  if (alerts.length === 0) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9990] flex flex-col gap-2 w-[90vw] max-w-lg pointer-events-none">
      {alerts.map((alert) => {
        const config = ALERT_TYPES[alert.type] || ALERT_TYPES.SCORE_CHANGE;

        return (
          <div
            className={`pointer-events-auto flex items-center gap-3 px-5 py-3.5 rounded-2xl border ${config.accent} ${config.bg} backdrop-blur-xl shadow-2xl shadow-black/40 animate-[slideDown_300ms_ease-out]`}
            key={alert.id}
          >
            {alert.logo && (
              <img
                alt=""
                className="w-8 h-8 object-contain flex-shrink-0"
                src={alert.logo}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-semibold truncate">
                {alert.message}
              </p>
            </div>
            <button
              className="text-white/30 hover:text-white/60 transition-colors flex-shrink-0"
              onClick={() => dismissAlert(alert.id)}
            >
              <i className="fas fa-times text-xs" />
            </button>
          </div>
        );
      })}

      <style>
        {`
          @keyframes slideDown {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};

export default SportsAlertBanner;
